import { LINKS, CAMPAIGN_IMAGES } from "../../config/links";

const PRODUCTS = [
  {
    id: "income-assessment",
    eyebrow: "Free Assessment",
    name: "Digital Income Mastery Questionnaire",
    description:
      "Discover where you are financially, digitally, and strategically — and uncover your next best move.",
    cta: {
      label: "Get Your Free Assessment",
      href: LINKS.digitalIncomeAssessment,
      testid: "start-here-cta-assessment",
    },
  },
  {
    id: "income-blueprint",
    eyebrow: "Foundational",
    name: "The Digital Income Blueprint",
    description:
      "Build and monetize digital income streams online — through branding, automation, content, and scalable systems.",
    cta: {
      label: "Start Building Today",
      href: LINKS.digitalIncomeBlueprint,
      testid: "start-here-cta-blueprint",
    },
  },
];

export default function StartHere() {
  return (
    <section
      id="start-here"
      data-testid="start-here-section"
      className="relative py-24 sm:py-32 overflow-hidden"
    >
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute top-1/4 left-1/3 h-[400px] w-[600px] bg-amber-500/[0.05] blur-[120px] rounded-full" aria-hidden />
      </div>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="reveal flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-amber-500/70" />
              <span className="font-sans-body uppercase text-[11px] tracking-[0.32em] text-amber-400">
                Start Here
              </span>
            </div>
            <h2
              data-testid="start-here-headline"
              className="reveal font-display font-light text-white text-4xl sm:text-5xl lg:text-[56px] leading-[1.05] tracking-[-0.01em] text-balance"
              style={{ transitionDelay: "80ms" }}
            >
              Simple entry tools for{" "}
              <span className="italic text-amber-400">clarity & momentum.</span>
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p
              className="reveal text-slate-300 text-base sm:text-lg leading-relaxed"
              style={{ transitionDelay: "140ms" }}
            >
              Stop chasing. Start mapping. These low-friction tools give you
              the operational picture before you commit to anything bigger.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Two product cards */}
          {PRODUCTS.map((p, i) => (
            <div
              key={p.id}
              data-testid={`start-here-card-${p.id}`}
              className="reveal group bg-[#0e1e44]/70 border border-white/10 hover:border-amber-500/40 p-8 lg:p-10 flex flex-col transition-all duration-400 hover:-translate-y-1"
              style={{ transitionDelay: `${120 + i * 90}ms` }}
            >
              <div className="flex items-center justify-between mb-6">
                <span className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400">
                  {p.eyebrow}
                </span>
                <span className="font-display text-xl text-slate-500">
                  0{i + 1}
                </span>
              </div>
              <h3 className="font-display text-2xl sm:text-3xl text-white leading-tight">
                {p.name}
              </h3>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed flex-1">
                {p.description}
              </p>
              <a
                href={p.cta.href}
                data-testid={p.cta.testid}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 inline-flex items-center justify-center gap-3 bg-amber-600 hover:bg-amber-500 text-slate-950 font-semibold px-6 py-4 text-[12px] uppercase tracking-[0.18em] transition-all duration-300 hover:-translate-y-0.5"
              >
                {p.cta.label}
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M13 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          ))}
        </div>

        {/* Roadrunner image — full-width cinematic banner */}
        <div
          data-testid="start-here-roadrunner-card"
          className="reveal mt-10 relative overflow-hidden border border-white/10 group"
        >
          <div className="grid grid-cols-1 lg:grid-cols-12 items-stretch">
            <div className="lg:col-span-7 relative bg-[#0e1e44]/60 overflow-hidden">
              <img
                src={CAMPAIGN_IMAGES.roadrunner}
                alt="Stop chasing. Start closing."
                className="w-full h-auto object-contain"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-white/5 pointer-events-none" />
            </div>
            <div className="lg:col-span-5 p-8 lg:p-12 flex flex-col justify-center bg-gradient-to-br from-[#0a1733] to-[#0e1e44]/80 border-t lg:border-t-0 lg:border-l border-white/10">
              <div className="font-sans-body uppercase text-[10px] tracking-[0.3em] text-amber-400 mb-3">
                Still chasing?
              </div>
              <h3 className="font-display text-3xl sm:text-4xl lg:text-[40px] text-white leading-tight">
                Stop chasing.{" "}
                <span className="italic text-amber-400">Start closing.</span>
              </h3>
              <p className="mt-4 text-sm sm:text-base text-slate-300 leading-relaxed max-w-md">
                The advisor stuck in roadrunner mode isn't lazy — they're
                under-systemized. These free tools give you the operational
                picture before you commit to anything bigger.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
